import {parseISO} from 'date-fns'
import {getFormattedDate} from '@app/util-functions'
import type {UserInterface} from '@shared/interfaces/user'

/**
 * Minimum year to consider a date as valid.
 * Used to filter out dates that result from JavaScript incorrectly parsing
 * numeric strings as years (e.g., "74.04" being parsed as April 1974).
 */
const MIN_VALID_YEAR = 2000

/**
 * Checks if a string is actually a date string, not a numeric value that
 * JavaScript's Date.parse() might incorrectly interpret as a date.
 *
 * This is important because JavaScript's Date.parse() has quirky behavior:
 * - "74.04" is parsed as April 1974 (74 = year, .04 = month)
 * - "91.5" is parsed as May 1991
 * - These are relative time values (minutes) that should NOT be treated as dates
 *
 * @param value The string value to check
 * @returns true if the value is a proper date string, false otherwise
 *
 * @example
 * isActualDateString("2026-02-05T20:43:00Z") // true - ISO date
 * isActualDateString("2026-2-5 20:43:00")    // true - formatted date
 * isActualDateString("74.04")                 // false - numeric (minutes)
 * isActualDateString("32.5")                  // false - numeric (minutes)
 * isActualDateString("-15.3")                 // false - negative numeric
 */
export const isActualDateString = (value: string): boolean => {
  if (!value || typeof value !== 'string') {
    return false
  }

  // Trim whitespace
  const trimmed = value.trim()

  // Quick check: if it's a short string, it's likely a numeric value, not a date
  // Date strings like "2026-02-05" have at least 10 characters
  if (trimmed.length < 8) {
    return false
  }

  // Check if the string looks like a numeric/relative time value
  // These patterns should NOT be treated as dates:
  // - "74.04", "32.5", "-15.3" (relative minutes with decimals)
  // - "1234" (pure numbers)
  // - "-1234.56" (negative numbers with decimals)
  const numericPattern = /^-?\d+(\.\d+)?$/
  if (numericPattern.test(trimmed)) {
    return false
  }

  // Check if it starts with a valid 4-digit year (2000+)
  // This catches ISO format: "2026-02-05T20:43:00Z" or "2026-02-05 20:43:00"
  const isoYearPattern = /^(2\d{3})-/
  if (isoYearPattern.test(trimmed)) {
    const parsed = Date.parse(trimmed)
    return !isNaN(parsed)
  }

  // Check for date-fns formatted dates like "2026-2-5 20:43:00" (y-MM-d H:mm:ss format)
  // These have a 4-digit year followed by hyphen and single or double digit month
  const formattedDatePattern = /^(2\d{3})-\d{1,2}-\d{1,2}\s+\d{1,2}:\d{2}/
  if (formattedDatePattern.test(trimmed)) {
    const parsed = Date.parse(trimmed)
    return !isNaN(parsed)
  }

  // For any other format, try to parse and validate the year
  const parsed = Date.parse(trimmed)
  if (isNaN(parsed)) {
    return false
  }

  // Additional validation: ensure the parsed year is reasonable
  // This catches edge cases where JavaScript parses numeric-like strings as old dates
  const date = new Date(parsed)
  const year = date.getFullYear()

  return year >= MIN_VALID_YEAR
}

/**
 * Formats a date string using user's locale preference
 * @param dateString ISO date string
 * @returns Formatted date and time string
 */
export const formatDateTime = (dateString: string, user: UserInterface | null = null): string => {
  try {
    const date = parseISO(dateString)
    return getFormattedDate(date, user, {
      showDate: true,
      showTime: true,
      showYear: true,
    })
  } catch (_error) {
    console.error('Error formatting date and time:', error)
    return 'Invalid date'
  }
}

/**
 * The time of day only, in the user's locale.
 *
 * For "last read at" markers, where the date is always today and repeating it
 * costs the width the caption is trying not to take. Seconds are shown: this
 * labels something that changes every few seconds, and a stamp that only moves
 * once a minute reads as frozen.
 */
export const formatTime = (
  value: string | number | Date,
  user: UserInterface | null = null,
): string => {
  try {
    const date = value instanceof Date ? value : new Date(value)
    return getFormattedDate(date, user, {
      showDate: false,
      showTime: true,
      showSeconds: true,
    })
  } catch (_error) {
    return ''
  }
}

/**
 * Formats a date range into a readable string using user's locale preference
 * @param startDate ISO date string for start date
 * @param endDate ISO date string for end date
 * @returns Formatted date range string
 */
export const formatDateTimeRange = (startDate: string, endDate: string): string => {
  try {
    let user: UserInterface | null = null
    try {
      const userProfileString = localStorage.getItem('userProfile')

      if (userProfileString) {
        user = JSON.parse(userProfileString)
      }
    } catch (e) {
      console.error('Error getting user from localStorage:', e)
    }

    const formattedStart = formatDateTime(startDate, user)
    const formattedEnd = formatDateTime(endDate, user)
    return `${formattedStart} - ${formattedEnd}`
  } catch (_error) {
    console.error('Error formatting date range:', error)
    return 'Invalid date range'
  }
}

/**
 * Checks if a date is valid
 * @param date ISO date string or Date object to validate
 * @returns Boolean indicating if date is valid
 */
export function isValidDate(date: string): boolean
export function isValidDate(date: Date): boolean
export function isValidDate(date: string | Date): boolean {
  try {
    if (typeof date === 'string') {
      return !Number.isNaN(parseISO(date).getTime())
    }
    return date instanceof Date && !Number.isNaN(date.getTime())
  } catch (_error) {
    return false
  }
}

/**
 * Calculates and formats the duration between two dates
 * @param startDate ISO date string for start date
 * @param endDate ISO date string for end date
 * @returns Formatted duration string in "Xh Ym" format
 */
export const formatDuration = (startDate: string, endDate: string): string => {
  try {
    const start = parseISO(startDate)
    const end = parseISO(endDate)

    const durationMs = end.getTime() - start.getTime()

    const hours = Math.floor(durationMs / (1000 * 60 * 60))
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60))

    if (hours === 0) {
      return `${minutes}m`
    }

    return `${hours}h ${minutes}m`
  } catch (_error) {
    console.error('Error calculating duration:', error)
    return 'Invalid duration'
  }
}

/**
 * Normalizes a date value to Date | null for MUI DateTimePicker compatibility
 * Handles string dates, Date objects, and null values
 * @param value Date string, Date object, or null
 * @returns Date object or null
 */
export const normalizeDateForPicker = (value: Date | string | null | undefined): Date | null => {
  if (!value) {
    return null
  }

  if (typeof value === 'string') {
    try {
      const date = new Date(value)
      return date.toString() === 'Invalid Date' ? null : date
    } catch {
      return null
    }
  }

  if (value instanceof Date) {
    return value.toString() === 'Invalid Date' ? null : value
  }

  return null
}
